// src/library/modules/userTemplateCreator.ts

import type { UserPresetTemplate as BaseUserPresetTemplate } from "@/types/userPresetTemplate";
import type { UserComponentJson } from "@/types/userComponent";
import type { ModuleOutput } from "../typeDefinations";
import { UserModule } from "./UserModule";

type AnyCreator = any;

type UserPresetTemplate = BaseUserPresetTemplate & {
  extras?: {
    moduleIdx?: number;
    [key: string]: any;
  };
};


export function createUserTemplateCreator(
  template: UserPresetTemplate,
  baseCreator: AnyCreator
): AnyCreator | null {
  console.log("[userTemplateCreator] preset called with", template, baseCreator);

  if (!baseCreator) {
    console.error("[userTemplateCreator] baseCreator is falsy");
    return null;
  }

  const wrapper: AnyCreator = Object.create(baseCreator);

  
  if (!template.extras) template.extras = {};

  
  if (typeof baseCreator.getModuleIndex === "number") {
    template.extras.moduleIdx = baseCreator.getModuleIndex;
  } else if (
    baseCreator.pendingModule &&
    typeof baseCreator.pendingModule.getModuleIndex === "number"
  ) {
    template.extras.moduleIdx = baseCreator.pendingModule.getModuleIndex;
  }

  wrapper.isUserTemplate = true;
  wrapper.isUserComponent = false;
  wrapper.templateId = template.id;
  wrapper.baseModuleIdx = template.baseModuleIdx;

 
  wrapper.baseModuleName = baseCreator.moduleName;

  wrapper.displayName = template.name;
  wrapper.moduleName = template.name;
  wrapper.description =
    (baseCreator.description || baseCreator.moduleName || "Custom") +
    " (custom preset)";

  wrapper.resetModule = function () {
   
    if (typeof baseCreator.resetModule === "function") {
      baseCreator.resetModule();
    }

    
    const basePending = baseCreator.pendingModule;
    wrapper.pendingModule =
      basePending && typeof basePending.clone === "function"
        ? basePending.clone()
        : JSON.parse(JSON.stringify(basePending || {}));

    
    if (wrapper.pendingModule && template.properties) {
      if (!wrapper.pendingModule.properties) {
        wrapper.pendingModule.properties = {};
      }
      const targetProps = wrapper.pendingModule.properties;
      const srcProps = template.properties;
      for (const key of Object.keys(srcProps)) {
        if (targetProps[key]) {
          targetProps[key] = {
            ...targetProps[key],
            ...srcProps[key]
          };
        }
      }
    }

    
    if (typeof wrapper.updatePendingModule === "function") {
      wrapper.updatePendingModule();
    }
  };

  
const baseCreateModule =
  typeof baseCreator.createModule === "function" ? baseCreator.createModule : null;

const baseCreateInstance =
  typeof baseCreator.createInstance === "function" ? baseCreator.createInstance : null;

wrapper.createModule = function (position: number[] | undefined, layer: string) {
  const ins = baseCreateModule
    ? baseCreateModule.call(wrapper, position, layer)
    : baseCreateInstance
      ? baseCreateInstance.call(wrapper, position, layer)
      : null;

  if (ins) {
    (ins as any).templateId = wrapper.templateId;
  }
  return ins;
};

wrapper.createInstance = function (position: number[], layer: string) {
  
  const ins = baseCreateInstance ? baseCreateInstance.call(wrapper, position, layer) : wrapper.createModule(position, layer);
  if (ins) {
    (ins as any).templateId = wrapper.templateId;
  }
  return ins;
};

  
  wrapper.resetModule();
  return wrapper;
}


export function createUserComponentCreator(
  component: UserComponentJson,
  initialize = true
): AnyCreator {
  const creator: AnyCreator = {
    isUserComponent: true,
    componentJson: component,

    moduleName: component.name,
    displayName: component.name,
    description: component.extras?.description || "User-defined module",

    
    getModuleIndex() {
      return component.extras?.moduleIdx ?? 1000;
    },

    pendingModule: null as any,

    
    resetModule() {
      const pos = component.base?.defaultPosition
        ? [...component.base.defaultPosition]
        : [5000, 5000];

      const layer = component.base?.defaultLayer ?? "Lv0";

      this.pendingModule = new UserModule(component, pos, layer, 0);
    },

  
    updatePendingModule() {
      if (!this.pendingModule) return;
      this.pendingModule.updateRender();
    },

    updatePendingModulePosition(pos: number[]) {
      if (!this.pendingModule || !Array.isArray(pos)) return;
      this.pendingModule.position = [pos[0], pos[1]];
      this.pendingModule.updateRender();
    },

  
    createModule(position: number[] | undefined, layer: string) {
      
      console.log(
        "[UserComponentCreator.createModule] position (arg) =",
        position,
        "pending position =",
        this.pendingModule && this.pendingModule.position
      );

      
      let pos: number[];
      if (this.pendingModule && Array.isArray(this.pendingModule.position)) {
        pos = [...this.pendingModule.position];
      } else if (Array.isArray(position) && position.length >= 2) {
       
        pos = position;
      } else {
        pos = [0, 0];
      }

      
      const rot = this.pendingModule ? this.pendingModule.rotation : 0;

      
      const initialProperties = this.pendingModule?.properties
        ? Object.fromEntries(
            Object.entries(this.pendingModule.properties).map(([key, prop]) => [
              key,
              (prop as any).value
            ])
          )
        : undefined;

      return new UserModule(
        component,
        pos,
        layer,
        rot,
        undefined,
        initialProperties
      );
    },

    
    createInstance(position: number[], layer: string) {
      return this.createModule(position, layer);
    },

   
        
    parseModule(content: ModuleOutput) {
      return UserModule.parse(component, content);
    },

   
    parse(content: ModuleOutput) {
      return UserModule.parse(component, content);
    }

  };

  if (initialize) creator.resetModule();
  return creator;
}

